import { createSlice } from "@reduxjs/toolkit";
import { Dispatch } from "redux";
import axios from "../utils/axios.tsx";

export interface ApiNotification {
  id: string;
  message: { text: string } & Record<string, string>;
  read: boolean;
}

export interface NotificationsState {
  byId: Record<string, ApiNotification>;
  allIds: string[];
  loading: boolean;
  error: string | null;
  viewing: boolean;
}

const initialState: NotificationsState = {
  byId: {},
  allIds: [],
  loading: false,
  error: null,
  viewing: false,
};

const notificationsSlice = createSlice({
  name: "notifications",
  initialState,
  reducers: {
    setLoading(state, action) {
      state.loading = action.payload;
    },
    setError(state, action) {
      state.error = action.payload;
    },
    loadAll(state, action) {
      state.byId = {};
      state.allIds = [];
      for (const n of action.payload as ApiNotification[]) {
        state.byId[n.id] = n;
        state.allIds.push(n.id);
      }
    },
    updateOne(state, action) {
      const { id, updateFields } = action.payload as {
        id: string;
        updateFields: Partial<ApiNotification>;
      };
      if (state.byId[id]) {
        state.byId[id] = { ...state.byId[id], ...updateFields };
      }
    },
    setViewing(state, action) {
      state.viewing = action.payload as boolean;
    },
  },
});

export const notificationsActions = notificationsSlice.actions;
export default notificationsSlice.reducer;

export const fetchNotifications = () => {
  return async (dispatch: Dispatch) => {
    dispatch(notificationsActions.setLoading(true));
    dispatch(notificationsActions.setError(null));
    const sendRequest = async () => {
      const response = await axios.get("/v1/notification/", {
        withCredentials: true,
      });
      if (response.status !== 200) {
        dispatch(notificationsActions.setError("Error fetching notifications"));
      } else {
        dispatch(
          notificationsActions.loadAll(
            (response.data.objects || []) as ApiNotification[],
          ),
        );
      }
    };
    try {
      await sendRequest();
    } catch (error) {
      dispatch(notificationsActions.setError("Error fetching notifications"));
    } finally {
      dispatch(notificationsActions.setLoading(false));
    }
  };
};

export const markNotificationRead = (id: string) => {
  return async (dispatch: Dispatch) => {
    const sendRequest = async () => {
      const response = await axios.post(`/v1/notification/${id}/read/`, {
        withCredentials: true,
      });
      if (response.status !== 200) {
      } else {
        dispatch(
          notificationsActions.updateOne({ id, updateFields: { read: true } }),
        );
      }
    };

    try {
      // Optimistic update for better UX
      dispatch(
        notificationsActions.updateOne({ id, updateFields: { read: true } }),
      );
      await sendRequest();
    } catch (error) {
      // Revert optimistic update on failure
      dispatch(
        notificationsActions.updateOne({ id, updateFields: { read: false } }),
      );
    }
  };
};
